import * as React from "react";
import { Box, Button, useMediaQuery, useTheme } from "@mui/material";
import CervinoText from "../../typography/CervinoText";
import ShimmerImage from "../../animations/ShimmerImage";

/**
 * TwoByTwoImageDataCarousel (2-up always, RTL swipe on mobile)
 * - Always renders 2 cards per page (xs included)
 * - Mobile swipe is RIGHT->LEFT to go NEXT (and LEFT->RIGHT to go PREV)
 * - Orientation "auto": lgUp => vertical, else horizontal (same as your original)
 * - onIndexChange returns the FIRST item index of the current page
 */
export default function TwoByTwoImageDataCarousel({
                                                   items = [],
                                                   initialIndex = 0,
                                                   orientation = "auto",
                                                   height,
                                                   onIndexChange,
                                                   mobileSwipeDirection = "rtl", // "rtl" | "ltr" (default rtl per your requirement)
                                               }) {
    const theme = useTheme();
    const lgDown = useMediaQuery(theme.breakpoints.down("lg"));
    const lgUp = useMediaQuery(theme.breakpoints.up("lg"));

    const resolvedOrientation = React.useMemo(() => {
        if (orientation === "horizontal") return "horizontal";
        if (orientation === "vertical") return "vertical";
        return lgUp ? "vertical" : "horizontal";
    }, [orientation, lgUp]);

    const canSwipe = lgDown;

    const itemsPerPage = 2;
    const count = items.length;

    const pageCount = React.useMemo(() => {
        if (!count) return 0;
        return Math.ceil(count / itemsPerPage);
    }, [count]);

    const clampPage = React.useCallback(
        (p) => {
            if (!pageCount) return 0;
            return ((p % pageCount) + pageCount) % pageCount;
        },
        [pageCount]
    );

    const clampItemIndex = React.useCallback(
        (i) => {
            if (!count) return 0;
            return ((i % count) + count) % count;
        },
        [count]
    );

    const itemIndexToPage = React.useCallback(
        (itemIndex) => Math.floor(clampItemIndex(itemIndex) / itemsPerPage),
        [clampItemIndex]
    );

    const pageToStartIndex = React.useCallback(
        (p) => clampPage(p) * itemsPerPage,
        [clampPage]
    );

    const [page, setPage] = React.useState(() => clampPage(itemIndexToPage(initialIndex)));

    const [anim, setAnim] = React.useState(() => {
        const p = clampPage(itemIndexToPage(initialIndex));
        return { from: p, to: p, phase: "idle", dir: 1 };
    });

    React.useEffect(() => {
        setPage((prev) => clampPage(prev));
        setAnim((a) => ({ ...a, from: clampPage(a.from), to: clampPage(a.to) }));
    }, [clampPage]);

    const goToPage = React.useCallback(
        (nextRaw) => {
            if (!pageCount) return;
            const next = clampPage(nextRaw);
            if (next === page) return;

            const dir = next > page ? 1 : -1;

            setAnim({ from: page, to: next, phase: "animating", dir });
            setPage(next);

            onIndexChange?.(pageToStartIndex(next));
        },
        [pageCount, clampPage, page, onIndexChange, pageToStartIndex]
    );

    const next = React.useCallback(() => goToPage(page + 1), [goToPage, page]);
    const prev = React.useCallback(() => goToPage(page - 1), [goToPage, page]);

    // Swipe logic
    const touch = React.useRef({ startX: 0, startY: 0, active: false });

    const onTouchStart = React.useCallback(
        (e) => {
            if (!canSwipe) return;
            const t = e.touches[0];
            touch.current = { startX: t.clientX, startY: t.clientY, active: true };
        },
        [canSwipe]
    );

    const onTouchEnd = React.useCallback(
        (e) => {
            if (!canSwipe || !touch.current.active) return;
            touch.current.active = false;

            const t = e.changedTouches[0];
            const dx = t.clientX - touch.current.startX;
            const dy = t.clientY - touch.current.startY;

            const threshold = 40;
            const absX = Math.abs(dx);
            const absY = Math.abs(dy);

            const isMobileRtl = mobileSwipeDirection === "rtl";

            if (resolvedOrientation === "horizontal") {
                if (absX < threshold || absX < absY) return;

                // dx < 0 means finger moved left (right->left swipe)
                // requirement: on mobile we go right->left to go NEXT
                if (isMobileRtl) {
                    dx < 0 ? next() : prev();
                } else {
                    dx < 0 ? next() : prev(); // same as default; kept for symmetry if you ever flip logic
                }
            } else {
                if (absY < threshold || absY < absX) return;
                // vertical remains standard: swipe up => next, down => prev
                dy < 0 ? next() : prev();
            }
        },
        [canSwipe, resolvedOrientation, next, prev, mobileSwipeDirection]
    );

    const duration = 420;

    const handleTransitionEnd = React.useCallback(() => {
        setAnim((a) => (a.phase === "animating" ? { ...a, phase: "idle", from: a.to } : a));
    }, []);

    const isAnimating = anim.phase === "animating" && anim.from !== anim.to;

    const currentExit = React.useMemo(() => {
        if (!isAnimating) return "translate3d(0,0,0)";

        if (resolvedOrientation === "horizontal") {
            return anim.dir === 1 ? "translate3d(-100%,0,0)" : "translate3d(100%,0,0)";
        }
        return anim.dir === 1 ? "translate3d(0,-100%,0)" : "translate3d(0,100%,0)";
    }, [isAnimating, resolvedOrientation, anim.dir]);

    const incomingStart = React.useMemo(() => {
        if (!isAnimating) return "translate3d(0,0,0)";

        if (resolvedOrientation === "horizontal") {
            return anim.dir === 1 ? "translate3d(100%,0,0)" : "translate3d(-100%,0,0)";
        }
        return anim.dir === 1 ? "translate3d(0,100%,0)" : "translate3d(0,-100%,0)";
    }, [isAnimating, resolvedOrientation, anim.dir]);

    const getPageItems = React.useCallback(
        (p) => {
            const start = pageToStartIndex(p);
            const out = [];
            for (let i = 0; i < itemsPerPage; i++) {
                const idx = start + i;
                if (idx < count) out.push(items[idx]);
            }
            return out;
        },
        [pageToStartIndex, items, count]
    );

    const renderCard = (item, key) => (
        <Box
            key={key}
            sx={{
                borderRadius: 4,
                borderColor: "divider",
                boxShadow: theme.shadows[1],
                p: 1,
                display: "flex",
                flexDirection: { xs: "column", lg: "row" },
                gap: 1,
                height: "100%",
                overflow: "hidden",
            }}
        >
            {/* Image */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: { xs: "center", lg: "flex-start" },
                }}
            >
                <Box
                    sx={{
                        width: { xs: "100%", lg: 200, xl: 200 },
                        height: { xs: "100%", sm: 180, md: 200, lg: "100%", xl: "100%" },
                        borderRadius: 3,
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flex: "0 0 auto",
                        mx: { xs: "auto", lg: 0 },
                    }}
                >
                    {item?.imageUrl ? (
                        <ShimmerImage
                            src={item.imageUrl}
                            alt={item.title}
                            height="100%"
                            width="100%"
                            borderRadius={5}
                            showBorder
                        />
                    ) : null}
                </Box>
            </Box>

            {/* Text */}
            <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
                <CervinoText
                    sx={{
                        fontWeight: 900,
                        fontSize: { xs: 30, sm: 34, md: 38, lg: 40, xl: 40 },
                        color: theme.palette.primary.main,
                        textTransform: "uppercase",
                        textAlign: "left",
                    }}
                >
                    {item.title}
                </CervinoText>

                {item.subtitle && (
                    <CervinoText
                        sx={{
                            fontWeight: 700,
                            fontSize: { xs: 14, sm: 18, md: 20, lg: 24, xl: 24 },
                            color: theme.palette.primary.main,
                            textAlign: "left",
                        }}
                    >
                        {item.subtitle}
                    </CervinoText>
                )}

                {item.description && (
                    <CervinoText
                        sx={{
                            fontWeight: 500,
                            fontSize: { xs: 14, sm: 18, md: 18, lg: 18, xl: 20 },
                            color: "#000",
                            textAlign: "justify",
                        }}
                    >
                        {item.description}
                    </CervinoText>
                )}
            </Box>
        </Box>
    );


    const renderPage = (p) => {
        const pageItems = getPageItems(p);

        return (
            <Box sx={{ width: "100%", height: "100%", boxSizing: "border-box", p: { xs: 0.5, sm: 1 } }}>
                <Box
                    sx={{
                        height: "100%",
                        width: "100%",
                        display: "grid",
                        gap: { xs: 1, sm: 2 },
                        gridTemplateColumns: { xs: "1fr 1fr", lg: "1fr" },
                        alignItems: "stretch",
                    }}
                >
                    {pageItems.map((item, i) => renderCard(item, `${item?.title ?? "item"}-${p}-${i}`))}

                    {/* keep layout consistent when last page has only 1 item */}
                    {pageItems.length === 1 && <Box />}
                </Box>
            </Box>
        );
    };


    const pageLabels = React.useMemo(() => {
        const labels = [];
        for (let p = 0; p < pageCount; p++) {
            const start = p * itemsPerPage;
            const a = items[start];
            const b = items[start + 1];

            const aLabel = a?.buttonTitle || a?.title || `Item ${start + 1}`;
            const bLabel = b?.buttonTitle || b?.title;

            labels.push(bLabel ? `${aLabel} - ${bLabel}` : `${aLabel}`);
        }
        return labels;
    }, [items, pageCount]);

    const currentPageItems = getPageItems(anim.from);
    const incomingPageItems = getPageItems(anim.to);

    return (
        <Box sx={{ width: "100%" }}>
            {/* Navigation */}
            <Box
                sx={{
                    mb: 2,
                    display: "flex",
                    gap: { xs: 1, sm: 2, md: 3 },
                    flexWrap: "wrap",
                    justifyContent: "center",
                }}
            >
                {pageLabels.map((label, p) => {
                    const selected = p === page;
                    return (
                        <Button
                            key={`page-${p}`}
                            variant={selected ? "contained" : "outlined"}
                            onClick={() => goToPage(p)}
                            sx={{
                                flex: "0 0 auto",
                                borderRadius: 999,
                                textTransform: "none",
                                fontSize: { xs: 10, sm: 14, md: 14, lg: 14, xl: 14 },
                                fontWeight: selected ? 900 : 700,
                                px: { xs: 1, sm: 2 },
                                boxShadow: selected ? theme.shadows[1] : "none",
                            }}
                        >
                            {label}
                        </Button>
                    );
                })}
            </Box>

            {/* Viewport */}
            <Box
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
                sx={{
                    position: "relative",
                    height,
                    overflow: "hidden",
                }}
            >
                {currentPageItems.length > 0 && (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            transform: currentExit,
                            transition: isAnimating
                                ? `transform ${duration}ms cubic-bezier(.2,.8,.2,1)`
                                : "none",
                        }}
                        onTransitionEnd={handleTransitionEnd}
                    >
                        {renderPage(anim.from)}
                    </Box>
                )}

                {isAnimating && incomingPageItems.length > 0 && (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            transform: incomingStart,
                            animation: `carousel-in-${resolvedOrientation}-${anim.dir} ${duration}ms cubic-bezier(.2,.8,.2,1) forwards`,
                            pointerEvents: "none",
                        }}
                    >
                        {renderPage(anim.to)}
                    </Box>
                )}

                {/* keyframes */}
                <Box
                    sx={{
                        "@keyframes carousel-in-horizontal-1": {
                            from: { transform: "translate3d(100%,0,0)" },
                            to: { transform: "translate3d(0,0,0)" },
                        },
                        "@keyframes carousel-in-horizontal--1": {
                            from: { transform: "translate3d(-100%,0,0)" },
                            to: { transform: "translate3d(0,0,0)" },
                        },
                        "@keyframes carousel-in-vertical-1": {
                            from: { transform: "translate3d(0,100%,0)" },
                            to: { transform: "translate3d(0,0,0)" },
                        },
                        "@keyframes carousel-in-vertical--1": {
                            from: { transform: "translate3d(0,-100%,0)" },
                            to: { transform: "translate3d(0,0,0)" },
                        },
                    }}
                />
            </Box>
        </Box>
    );
}
